/* FLM — shared loading / empty / error states for the live-data screens.
   Visual language copied from the deck's empty panel (dashed border + eyebrow +
   body note + small CTA) so the additions read native to the design port. */
import React from 'react';

/** Centered pulsing-dot loader — the lightweight "fetching" state. */
export function LoadingPanel({ label, style = {} }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, padding: 30, flex: 1, minHeight: 0, ...style,
    }}>
      <span className="flm-dot" style={{ width: 8, height: 8, animation: 'flmPulseDot 1.6s infinite' }}></span>
      <span className="flm-mono" style={{ fontSize: 9, color: 'var(--text-3)' }}>{label}</span>
    </div>
  );
}

/** Bare shimmer block — the skeleton building primitive. */
export function Skeleton({ w = '100%', h = 14, r = 10, style = {} }) {
  return <span className="flm-skeleton" style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }} />;
}

/** Discover deck card placeholder — full-bleed photo block + name/meta bars. */
export function DeckCardSkeleton({ style = {} }) {
  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...style }}>
      <div className="flm-skeleton" style={{ flex: 1, minHeight: 0, borderRadius: 28 }} />
      <div style={{ position: 'absolute', insetInline: 22, bottom: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Skeleton w="62%" h={26} r={9} />
        <Skeleton w="40%" h={15} r={8} />
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Skeleton w={74} h={28} r={999} />
          <Skeleton w={92} h={28} r={999} />
          <Skeleton w={60} h={28} r={999} />
        </div>
      </div>
    </div>
  );
}

/** Chat / match list placeholder — avatar + two text lines per row. */
export function ListSkeleton({ rows = 6, avatar = true, style = {} }) {
  return (
    <div style={{ padding: '4px 18px', ...style }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 0' }}>
          {avatar ? <Skeleton w={50} h={50} r={999} style={{ flex: 'none' }} /> : null}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton w={`${55 + ((i * 7) % 30)}%`} h={14} />
            <Skeleton w={`${70 + ((i * 5) % 22)}%`} h={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Profile hub placeholder — avatar ring + name + 3 stats + a few section cards. */
export function ProfileSkeleton({ style = {} }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '10px 18px', ...style }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 4 }}>
        <Skeleton w={92} h={92} r={999} />
        <Skeleton w={150} h={20} />
        <div style={{ display: 'flex', gap: 22, marginTop: 2 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Skeleton w={32} h={18} />
              <Skeleton w={46} h={10} />
            </div>
          ))}
        </div>
      </div>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} w="100%" h={i === 0 ? 96 : 60} r={18} />
      ))}
    </div>
  );
}

/** Photo-grid placeholder (e.g. "who liked you") — N rounded tiles. */
export function GridSkeleton({ count = 6, cols = 2, ratio = '0.82', style = {} }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, padding: '4px 18px', ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flm-skeleton" style={{ width: '100%', aspectRatio: ratio, borderRadius: 20 }} />
      ))}
    </div>
  );
}

/** Designed empty/error panel — same markup as the deck-empty artboard state. */
export function StatePanel({ title, note, actionLabel, onAction, style = {} }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, border: '1px dashed var(--border-bright)', borderRadius: 24,
      textAlign: 'center', padding: 30, ...style,
    }}>
      <span className="eyebrow">{title}</span>
      {note ? <span style={{ fontFamily: 'var(--font-body)', color: 'var(--text-3)', fontSize: 13.5 }}>{note}</span> : null}
      {actionLabel ? (
        <button className="flm-cta is-sm" style={{ padding: '0 22px' }} onClick={onAction}>{actionLabel}</button>
      ) : null}
    </div>
  );
}
